import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, X, Wand2, Stamp, Search, RefreshCw, Trash2 } from "lucide-react";
import { zoomCard } from "@/components/CardZoom";

const STYLES = [
  { id: "comic", name: "Comic Legend", desc: "Vintage comic cover, halftone ink", tier: "common" },
  { id: "ice", name: "Cracked Ice", desc: "Shattered refractor, icy prismatic shards", tier: "common" },
  { id: "holo", name: "Ace Holo", desc: "Prismatic holographic foil", tier: "rare" },
  { id: "gold", name: "Goat Gold", desc: "24k gold luxury frame", tier: "rare" },
  { id: "ronin", name: "Bullpen Ronin", desc: "Japanese woodblock + gold leaf", tier: "rare" },
  { id: "chrome", name: "Chrome Refractor", desc: "Mirror chrome + rainbow refraction", tier: "elite" },
  { id: "galaxy", name: "Galaxy Prism", desc: "Nebula, starfields & prism shards", tier: "elite" },
  { id: "legend", name: "Bullpen Legend", desc: "Black & crimson bull crest", tier: "legendary" },
  { id: "neon", name: "Neon Lights", desc: "Cyberpunk neon glow", tier: "legendary" },
  { id: "champion", name: "Tournament Champion", desc: "Platinum + trophy-gold laurels", tier: "legendary" },
  { id: "marble", name: "Marble", desc: "Sculpted marble + gold inlay", tier: "icon" },
  { id: "shimmer", name: "Metal Shimmer", desc: "Liquid titanium color-shift", tier: "icon" },
  { id: "retro", name: "Retro Classic", desc: "Vintage 90s card design", tier: "icon" },
  { id: "graffiti", name: "Street Kings", desc: "Graffiti wildstyle & spray drips", tier: "icon" },
];

const TIERS = {
  common: { label: "Common", cls: "text-stone-400", activeCls: "border-stone-400 bg-stone-400/10 text-stone-300" },
  rare: { label: "Rare", cls: "text-sky-300", activeCls: "border-sky-400 bg-sky-400/10 text-sky-300" },
  elite: { label: "Elite", cls: "text-amber-300", activeCls: "border-amber-400 bg-amber-400/10 text-amber-300" },
  legendary: { label: "Legendary", cls: "text-violet-300", activeCls: "border-violet-400 bg-violet-400/10 text-violet-300" },
  icon: { label: "Icon", cls: "text-white", activeCls: "border-white bg-white/10 text-white" },
};

export default function CardForge() {
  const [photo, setPhoto] = useState(null);
  const [style, setStyle] = useState("champion");
  const [title, setTitle] = useState("");
  const [statsText, setStatsText] = useState("");
  const [notes, setNotes] = useState("");
  const [logoPos, setLogoPos] = useState("top-center");
  const [logoSize, setLogoSize] = useState("standard");
  const [draft, setDraft] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const [playerQ, setPlayerQ] = useState("");
  const [playerResults, setPlayerResults] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [roster, setRoster] = useState([]);
  const [dest, setDest] = useState("pool");
  const [cardType, setCardType] = useState("exclusive");
  const [mintGender, setMintGender] = useState("");
  const [marketPrice, setMarketPrice] = useState("10");
  const [marketCopies, setMarketCopies] = useState("5");
  const [bountyOn, setBountyOn] = useState(false);
  const [bountyPrize, setBountyPrize] = useState("10");
  const [bountyCopies, setBountyCopies] = useState("1");
  const [bountyPerks, setBountyPerks] = useState("");
  const [editionTotal, setEditionTotal] = useState("");
  const [houseCopyNum, setHouseCopyNum] = useState("1");
  const [backFact, setBackFact] = useState("");
  const [backStatsText, setBackStatsText] = useState("");
  const [backLoading, setBackLoading] = useState(false);
  const fileRef = useRef(null);
  const styleTier = STYLES.find((s) => s.id === (draft?.style || style))?.tier || "common";

  const parseStats = (txt) => txt.split("\n").map((l) => l.split("|").map((s) => s.trim()))
    .filter((p) => p.length >= 2 && p[0] && p[1]).map((p) => [p[0], p[1]]);

  const writeBack = async (draftTitle, type) => {
    setBackLoading(true);
    try {
      const { data } = await api.post("/admin/forge/preview-back", { title: draftTitle, card_type: type || cardType }, { timeout: 90000 });
      setBackFact(data.fact || "");
      setBackStatsText((data.stats || []).map(([l, v]) => `${l} | ${v}`).join("\n"));
    } catch { toast.error("Couldn't write the card-back fact — hit Rewrite to try again"); }
    finally { setBackLoading(false); }
  };

  useEffect(() => {
    setBackFact(""); setBackStatsText("");
    if (draft?.id) writeBack(draft.title || title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id]);

  const loadDrafts = () => api.get("/admin/forge/drafts").then(({ data }) => setDrafts(data)).catch(() => {});
  useEffect(() => {
    loadDrafts();
    api.get("/admin/royalty-roster").then(({ data }) => setRoster(data)).catch(() => {});
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 1024 / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setPhoto(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const pollJob = async (jobId) => {
    for (let i = 0; i < 80; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await api.get(`/admin/forge/jobs/${jobId}`);
      if (data.status === "done") return data.result;
      if (data.status === "error") throw new Error(data.detail);
    }
    throw new Error("Still forging — check your drafts in a minute");
  };

  const generate = async () => {
    setBusy("generate");
    try {
      const { data } = await api.post("/admin/forge/generate",
        { image: photo, style, title, stats_text: statsText, notes, logo_position: logoPos, logo_size: logoSize });
      const result = await pollJob(data.job_id);
      setDraft(result);
      loadDrafts();
      toast.success("Card forged — refine it or mint it into the pool");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const refine = async () => {
    setBusy("refine");
    try {
      const { data } = await api.post(`/admin/forge/${draft.id}/refine`, { feedback });
      const result = await pollJob(data.job_id);
      setDraft(result);
      setFeedback("");
      toast.success(`Refined — version ${result.versions}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const mint = async () => {
    setBusy("mint");
    try {
      await api.post(`/admin/forge/${draft.id}/mint`, {
        featured_player_id: featured?.id || null,
        destination: dest,
        market_price: Number(marketPrice) || 0,
        market_copies: Number(marketCopies) || 1,
        bounty_prize: bountyOn ? Number(bountyPrize) || 0 : 0,
        bounty_copies: bountyOn ? Number(bountyCopies) || 0 : 0,
        bounty_perks: bountyOn ? bountyPerks : "",
        edition_total: Number(editionTotal) || 0,
        card_type: cardType,
        house_copy_num: Number(houseCopyNum) || 0,
        back_fact: backFact.trim(),
        back_stats: parseStats(backStatsText),
        gender: mintGender,
      });
      const label = cardType === "exclusive" ? "BULLPEN EXCLUSIVE" : `${(TIERS[styleTier]?.label || "Common").toUpperCase()} CARD`;
      const edTxt = Number(editionTotal) > 0 ? ` Hand-numbered edition of ${editionTotal} — badges 1/${editionTotal} through ${editionTotal}/${editionTotal}.` : "";
      toast.success((dest === "market"
        ? `${label} listed — ${Number(editionTotal) > 0 ? editionTotal : marketCopies} copies on the Marketplace at ${marketPrice} BLC${bountyOn ? " + bounty live in packs" : ""}!`
        : dest === "vault"
        ? "Card minted and held in your Personal Vault — release it from the Card Vault Command Center whenever you're ready."
        : `${label} minted into the pack pool!`) + edTxt);
      setDraft(null); setPhoto(null); setFeatured(null); setTitle(""); setStatsText(""); setNotes(""); setBountyOn(false); setBountyPerks(""); setEditionTotal("");
      loadDrafts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const discard = async (id) => {
    try { await api.delete(`/admin/forge/${id}`); if (draft?.id === id) setDraft(null); loadDrafts(); } catch {}
  };

  const searchPlayers = async (q) => {
    setPlayerQ(q);
    if (q.length < 2) { setPlayerResults([]); return; }
    try {
      const { data } = await api.get(`/members/search?q=${encodeURIComponent(q)}`);
      setPlayerResults(data.slice(0, 6));
    } catch { setPlayerResults([]); }
  };

  return (
    <div className="space-y-5" data-testid="card-forge">
      <p className="text-xs text-zinc-500">
        Forge Bullpen-made special edition cards from your own photos (tournaments, leagues, candids). Write the name banner
        and bottom text yourself, add design notes, refine the result with feedback, then mint it into the pack pool or list
        it straight on the Marketplace at your price. Feature a royalty-roster player and their custom % is paid on every
        sale and pack pull. Generations are free.
      </p>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Inputs */}
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            {photo ? (
              <div className="relative shrink-0">
                <img src={photo} alt="upload" className="w-28 h-36 object-cover border border-pb-border" data-testid="forge-photo-preview" />
                <button data-testid="forge-photo-clear" onClick={() => setPhoto(null)}
                  className="absolute -top-2 -right-2 bg-black border border-pb-border p-1 hover:border-red-500"><X className="w-3 h-3 text-zinc-300" /></button>
              </div>
            ) : (
              <button data-testid="forge-upload-btn" onClick={() => fileRef.current?.click()}
                className="shrink-0 w-28 h-36 border border-dashed border-pb-border hover:border-pb-red flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-pb-red transition-colors">
                <Upload className="w-5 h-5" /><span className="text-[9px] uppercase tracking-widest font-bold text-center px-1">Upload photo</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="forge-file-input" />
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {STYLES.map((s) => (
                <button key={s.id} data-testid={`forge-style-${s.id}`} onClick={() => setStyle(s.id)} title={s.desc}
                  className={`text-left px-2.5 py-2 border transition-colors ${style === s.id ? "border-pb-red bg-pb-red/10" : "border-pb-border hover:border-zinc-500"}`}>
                  <div className="text-[10px] font-black uppercase leading-tight">{s.name}</div>
                  <div className={`text-[8px] uppercase tracking-widest ${TIERS[s.tier].cls}`}>{TIERS[s.tier].label}</div>
                </button>
              ))}
            </div>
          </div>
          <input data-testid="forge-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60}
            placeholder="Name banner text — e.g. SUMMER SLAM 2026"
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
          <input data-testid="forge-stats-input" value={statsText} onChange={(e) => setStatsText(e.target.value)} maxLength={120}
            placeholder="Bottom text panel — e.g. MEN'S DOUBLES CHAMPIONS | BULLPEN OPEN"
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
          <textarea data-testid="forge-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} rows={2}
            placeholder="Design notes (optional) — e.g. add falling confetti, stormy sky, make it feel epic"
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Logo Placement</label>
              <select data-testid="forge-logo-pos" value={logoPos} onChange={(e) => setLogoPos(e.target.value)}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red">
                <option value="top-center">Top Center</option>
                <option value="top-left">Top Left</option>
                <option value="top-right">Top Right</option>
                <option value="bottom-left">Bottom Left</option>
                <option value="bottom-center">Bottom Center</option>
                <option value="bottom-right">Bottom Right</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Logo Size</label>
              <select data-testid="forge-logo-size" value={logoSize} onChange={(e) => setLogoSize(e.target.value)}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red">
                <option value="discreet">Discreet — subtle seal</option>
                <option value="standard">Standard</option>
                <option value="bold">Bold — statement badge</option>
              </select>
            </div>
          </div>
          <p className="text-[11px] text-zinc-600 -mt-1">The official logo is rendered as a holographic foil authenticity seal — the card's security mark and branding in one.</p>
          <button data-testid="forge-generate-btn" onClick={generate} disabled={!photo || !!busy}
            className="flex items-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest px-6 py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
            {busy === "generate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {busy === "generate" ? "Forging… up to 2 min" : "Forge Card"}
          </button>
        </div>

        {/* Result */}
        <div>
          {draft ? (
            <div className="space-y-3" data-testid="forge-result">
              <img src={draft.url} alt="Forged card" className="w-full max-w-[280px] mx-auto border border-pb-red/40" data-testid="forge-result-img" />
              <div className="text-center text-[9px] uppercase tracking-widest text-zinc-500">Version {draft.versions}</div>
              <div className="flex gap-2">
                <input data-testid="forge-feedback-input" value={feedback} onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Edit it — e.g. make the frame darker, bigger title"
                  className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white focus:outline-none focus:border-pb-cyan" />
                <button data-testid="forge-refine-btn" onClick={refine} disabled={!feedback.trim() || !!busy}
                  className="shrink-0 flex items-center gap-1.5 border border-pb-cyan text-pb-cyan font-black uppercase tracking-widest px-4 py-2 text-[10px] hover:bg-pb-cyan hover:text-black transition-colors disabled:opacity-40">
                  {busy === "refine" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refine
                </button>
              </div>
              {roster.length > 0 && (
                <div data-testid="forge-roster-chips">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Royalty roster — tap to feature</div>
                  <div className="flex flex-wrap gap-1.5">
                    {roster.map((r) => (
                      <button key={r.player_id} data-testid={`forge-roster-chip-${r.player_id}`}
                        onClick={() => setFeatured(featured?.id === r.player_id ? null : { id: r.player_id, name: r.name, share_pct: r.share_pct })}
                        className={`px-2.5 py-1.5 border text-[10px] font-bold uppercase tracking-wide transition-colors ${
                          featured?.id === r.player_id ? "border-pb-red bg-pb-red/10 text-pb-red" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
                        {r.name} · {r.share_pct}%
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="relative">
                <div className="flex items-center gap-2 bg-pb-elevated border border-pb-border px-3">
                  <Search className="w-3.5 h-3.5 text-zinc-500" />
                  <input data-testid="forge-player-search" value={featured ? `${featured.name}${featured.share_pct != null ? ` — ${featured.share_pct}% royalty` : ""}` : playerQ}
                    onChange={(e) => { setFeatured(null); searchPlayers(e.target.value); }}
                    placeholder="Feature a player (roster % applies, otherwise 50/50)"
                    className="flex-1 bg-transparent py-2 text-sm text-white focus:outline-none" />
                  {featured && <button data-testid="forge-player-clear" onClick={() => { setFeatured(null); setPlayerQ(""); }}><X className="w-3.5 h-3.5 text-zinc-500" /></button>}
                </div>
                {playerResults.length > 0 && !featured && (
                  <div className="absolute z-10 w-full bg-pb-surface border border-pb-border max-h-40 overflow-y-auto">
                    {playerResults.map((p) => {
                      const rr = roster.find((r) => r.player_id === p.id);
                      return (
                        <button key={p.id} data-testid={`forge-player-opt-${p.id}`}
                          onClick={() => { setFeatured(rr ? { id: p.id, name: p.name, share_pct: rr.share_pct } : p); setPlayerResults([]); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-pb-elevated">
                          {p.name}{rr ? <span className="text-[9px] text-pb-red font-bold uppercase ml-2">Roster · {rr.share_pct}%</span> : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Card type / rarity tier */}
              <div data-testid="forge-cardtype-toggle">
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Card tier — the style you forged sets it, or crown it a chase card</div>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="forge-cardtype-style"
                    onClick={() => { setCardType("auto"); if (draft?.id) writeBack(draft.title || title, "auto"); }}
                    className={`text-left px-2.5 py-2 border transition-colors ${cardType !== "exclusive" ? (TIERS[styleTier]?.activeCls || "border-zinc-300 bg-white/5 text-zinc-200") : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
                    <div className="text-[10px] font-black uppercase">{TIERS[styleTier]?.label || "Common"} — Style Tier</div>
                    <div className="text-[8px] uppercase tracking-widest opacity-60">Pulls at {TIERS[styleTier]?.label || "common"}-tier odds</div>
                  </button>
                  <button data-testid="forge-cardtype-exclusive"
                    onClick={() => { setCardType("exclusive"); if (draft?.id) writeBack(draft.title || title, "exclusive"); }}
                    className={`text-left px-2.5 py-2 border transition-colors ${cardType === "exclusive" ? "border-pb-red bg-pb-red/10 text-pb-red" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
                    <div className="text-[10px] font-black uppercase">Bullpen Exclusive</div>
                    <div className="text-[8px] uppercase tracking-widest opacity-60">Chase card · exclusive odds</div>
                  </button>
                </div>
              </div>
              <div className="flex gap-2" data-testid="forge-destination-toggle">
                <button data-testid="forge-dest-pool" onClick={() => setDest("pool")}
                  className={`flex-1 text-left px-3 py-2 border transition-colors ${dest === "pool" ? "border-pb-red bg-pb-red/10" : "border-pb-border hover:border-zinc-500"}`}>
                  <div className="text-[10px] font-black uppercase">Pack Pool</div>
                  <div className="text-[8px] uppercase tracking-widest text-zinc-500">Hides in packs at exclusive odds</div>
                </button>
                <button data-testid="forge-dest-market" onClick={() => setDest("market")}
                  className={`flex-1 text-left px-3 py-2 border transition-colors ${dest === "market" ? "border-pb-cyan bg-pb-cyan/10" : "border-pb-border hover:border-zinc-500"}`}>
                  <div className="text-[10px] font-black uppercase">Marketplace</div>
                  <div className="text-[8px] uppercase tracking-widest text-zinc-500">Direct sale — you set the price</div>
                </button>
                <button data-testid="forge-dest-vault" onClick={() => setDest("vault")}
                  className={`flex-1 text-left px-3 py-2 border transition-colors ${dest === "vault" ? "border-zinc-300 bg-white/5" : "border-pb-border hover:border-zinc-500"}`}>
                  <div className="text-[10px] font-black uppercase">Hold in Vault</div>
                  <div className="text-[8px] uppercase tracking-widest text-zinc-500">Keep it private — release later</div>
                </button>
              </div>
              {dest !== "vault" && (
                <div className="border border-[#A3E635]/30 bg-[#A3E635]/5 p-3" data-testid="forge-edition-block">
                  <div className="flex items-center gap-3">
                    <div>
                      <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Numbered print run (optional)</label>
                      <input data-testid="forge-edition-total" type="number" min={0} max={1000} step={1} value={editionTotal}
                        placeholder="e.g. 10" onChange={(e) => setEditionTotal(e.target.value)}
                        className="w-24 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-[#A3E635]" />
                    </div>
                    <p className="text-[9px] text-zinc-500 flex-1">
                      Set a run of e.g. 10 and each copy gets a collector badge — 1/10, 2/10 … 10/10.
                      {dest === "market" ? " The run size becomes the number of copies listed." : " Packs stop pulling it forever once the last number is claimed."}
                    </p>
                  </div>
                  {Number(editionTotal) > 0 && (
                    <div className="mt-2 text-[9px] font-black uppercase tracking-widest text-[#A3E635]" data-testid="forge-edition-preview">
                      ✦ Hand-numbered edition of {editionTotal} — badges 1/{editionTotal} → {editionTotal}/{editionTotal}
                    </div>
                  )}
                </div>
              )}
              {cardType === "exclusive" && (
                <div className="border border-amber-400/30 bg-amber-400/5 p-3" data-testid="forge-house-copy-block">
                  <div className="flex items-center gap-3 flex-wrap">
                    {Number(editionTotal) > 0 && (
                      <div>
                        <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">House keeps #</label>
                        <input data-testid="forge-house-copy-num" type="number" min={1} max={Number(editionTotal)} step={1} value={houseCopyNum}
                          onChange={(e) => setHouseCopyNum(e.target.value)}
                          className="w-20 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-amber-400" />
                      </div>
                    )}
                    <p className="text-[9px] text-zinc-500 flex-1 min-w-[180px]">
                      <span className="text-amber-300 font-bold uppercase">The house always holds.</span>{" "}
                      {Number(editionTotal) > 0
                        ? `One numbered copy (#${houseCopyNum || "?"}/${editionTotal}) mints straight into your admin collection — only ${Math.max(0, Number(editionTotal) - 1)} go to players.`
                        : "One copy of this exclusive mints straight into your admin collection — yours to gift or sell anytime."}
                    </p>
                  </div>
                  {Number(editionTotal) > 0 && (Number(houseCopyNum) < 1 || Number(houseCopyNum) > Number(editionTotal)) && (
                    <div className="mt-1.5 text-[9px] font-bold uppercase tracking-widest text-red-400" data-testid="forge-house-copy-invalid">Pick a number between 1 and {editionTotal}</div>
                  )}
                </div>
              )}
              {dest === "market" && (
                <div className="space-y-2.5 border border-pb-cyan/30 bg-pb-cyan/5 p-3" data-testid="forge-market-options">
                  <div className="flex gap-3">
                    <div>
                      <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Price (BLC)</label>
                      <input data-testid="forge-market-price" type="number" min={0.5} step={0.5} value={marketPrice}
                        onChange={(e) => setMarketPrice(e.target.value)}
                        className="w-24 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-pb-cyan" />
                    </div>
                    <div>
                      <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Copies for sale</label>
                      <input data-testid="forge-market-copies" type="number" min={1} max={100} step={1} value={marketCopies}
                        onChange={(e) => setMarketCopies(e.target.value)}
                        className="w-24 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-pb-cyan" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="forge-bounty-toggle">
                    <input type="checkbox" checked={bountyOn} onChange={(e) => setBountyOn(e.target.checked)}
                      className="accent-[#F87171] w-3.5 h-3.5" data-testid="forge-bounty-checkbox" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-300">Also make it a bounty card (pullable from packs)</span>
                  </label>
                  {bountyOn && (
                    <div className="space-y-2" data-testid="forge-bounty-options">
                      <div className="flex gap-3">
                      <div>
                        <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Bounty prize (BLC)</label>
                        <input data-testid="forge-bounty-prize" type="number" min={0.5} step={0.5} value={bountyPrize}
                          onChange={(e) => setBountyPrize(e.target.value)}
                          className="w-24 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-pb-red" />
                      </div>
                      <div>
                        <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Winning copies</label>
                        <input data-testid="forge-bounty-copies" type="number" min={1} step={1} value={bountyCopies}
                          onChange={(e) => setBountyCopies(e.target.value)}
                          className="w-24 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-pb-red" />
                      </div>
                      </div>
                      <div>
                        <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Comes with (extra perks — shown to bounty hunters)</label>
                        <input data-testid="forge-bounty-perks" type="text" maxLength={200} value={bountyPerks}
                          placeholder="e.g. Free tournament entry + Bullpen hat"
                          onChange={(e) => setBountyPerks(e.target.value)}
                          className="w-full bg-pb-elevated border border-pb-border px-2 py-1.5 text-white text-xs focus:outline-none focus:border-amber-400" />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Card back fact — approve/edit before mint */}
              <div className="border border-amber-400/30 bg-amber-400/5 p-3 space-y-2" data-testid="forge-back-panel">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-amber-300">Card Back — approve or edit the fact before minting</div>
                  <button data-testid="forge-back-rewrite" onClick={() => writeBack(draft.title || title)} disabled={backLoading}
                    className="shrink-0 flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest border border-pb-border px-2.5 py-1.5 text-zinc-300 hover:border-pb-cyan hover:text-pb-cyan transition-colors disabled:opacity-50">
                    {backLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Rewrite with AI
                  </button>
                </div>
                {backLoading && !backFact ? (
                  <div className="flex items-center gap-2 text-[10px] text-zinc-500 py-2"><Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300" /> Writing the "Did You Know" fact…</div>
                ) : (
                  <>
                    <div>
                      <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">"Did You Know" fact — edit freely</label>
                      <textarea data-testid="forge-back-fact" value={backFact} onChange={(e) => setBackFact(e.target.value)} rows={4}
                        placeholder="The fact printed on the back of the card…"
                        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-xs text-white leading-relaxed focus:outline-none focus:border-amber-400" />
                    </div>
                    <div>
                      <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Back stats — one per line as Label | Value</label>
                      <textarea data-testid="forge-back-stats" value={backStatsText} onChange={(e) => setBackStatsText(e.target.value)} rows={4}
                        placeholder={"Pro Titles | 12\nTurned Pro | 2016"}
                        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-amber-400" />
                    </div>
                    {!backFact.trim() && <div className="text-[9px] font-bold uppercase tracking-widest text-amber-400" data-testid="forge-back-required">Approve a fact (or write one) to unlock minting</div>}
                  </>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="forge-gender-row">
                <span className="text-[8px] uppercase tracking-widest text-zinc-500">Card Wars gender (for gendered doubles):</span>
                {[["male", "♂ Male"], ["female", "♀ Female"], ["", "—"]].map(([v, lbl]) => (
                  <button key={v || "none"} data-testid={`forge-gender-${v || "none"}`} onClick={() => setMintGender(v)}
                    className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 border transition-colors ${mintGender === v
                      ? (v === "male" ? "border-sky-400 text-sky-300 bg-sky-400/10" : v === "female" ? "border-pink-400 text-pink-300 bg-pink-400/10" : "border-zinc-400 text-zinc-300")
                      : "border-pb-border text-zinc-600 hover:border-zinc-500"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button data-testid="forge-mint-btn" onClick={mint}
                  disabled={!!busy || backLoading || !backFact.trim() || (cardType === "exclusive" && Number(editionTotal) > 0 && (Number(houseCopyNum) < 1 || Number(houseCopyNum) > Number(editionTotal)))}
                  className="flex-1 flex items-center justify-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
                  {busy === "mint" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stamp className="w-4 h-4" />}
                  {dest === "market" ? "Mint & List on Marketplace" : dest === "vault" ? "Mint & Hold in Vault" : "Mint into Pack Pool"}
                </button>
                <button data-testid="forge-discard-btn" onClick={() => discard(draft.id)}
                  className="border border-pb-border text-zinc-400 px-4 text-xs font-bold uppercase tracking-widest hover:border-red-500 hover:text-red-400">Discard</button>
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[200px] border border-dashed border-pb-border flex items-center justify-center text-xs text-zinc-600 uppercase tracking-widest" data-testid="forge-empty">
              Forged card appears here
            </div>
          )}
        </div>
      </div>

      {drafts.length > 0 && (
        <div data-testid="forge-drafts">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Unminted drafts</div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {drafts.map((d) => (
              <div key={d.id} className="shrink-0 w-24 relative">
                <button onClick={() => setDraft(d)} data-testid={`forge-draft-${d.id}`} className="block w-full border border-pb-border hover:border-pb-red">
                  <img src={d.url} alt={d.title} className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in"
                    title="Tap to zoom" onClick={() => zoomCard(d.url, d.title)} />
                </button>
                <button onClick={() => discard(d.id)} data-testid={`forge-draft-delete-${d.id}`}
                  className="absolute -top-2 -right-2 bg-black border border-pb-border p-1 hover:border-red-500"><Trash2 className="w-3 h-3 text-zinc-400" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
